import React from 'react';
import Tooltip from '@/components/atoms/Tooltip';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { FaUndo } from 'react-icons/fa';
import { MdQrCode2 } from 'react-icons/md';
import { StatusBadge } from '@/components/atoms';

import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { getRole, ROLES } from '@/constant/users/user.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/smartCoolerProducts.testIds';
import { Cell, PillCell } from '@/components/atoms';
import AppButton from '@/components/atoms/AppButton';
import type { ColumnDef } from '@/components/organism/Table';
import type { ISmartCoolerProduct } from '@/services/smartCoolerProducts';
import brokenImage from '../../assets/images/No-Image-Available.png';
import { SMART_COOLER_COLUMNS } from './constant';

// ─── Column definitions ───────────────────────────────────────────────────────
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
  onEditClick: (item: ISmartCoolerProduct) => void,
  onDeleteClick: (item: ISmartCoolerProduct) => void,
  onActivateClick: (item: ISmartCoolerProduct) => void,
): ColumnDef<ISmartCoolerProduct>[] {
  return [
    // ── 5. Sub-Application — prominent pill ───────────────────────────────────
    {
      accessor: 'subapplication',
      header: SMART_COOLER_COLUMNS.SUBAPP,
      render: (item, idx) => {
        const name = item?.subapplication?.name;
        if (!name) return <Cell value={undefined} variant="muted" />;
        return (
          <PillCell
            testId={tid(TID.COL_SUBAPP(`${idx}`))}
            value={name}
            preset="subapp"
            dot
            className="uppercase tracking-widest border font-bold"
          />
        );
      },
    },
    // ── 1. Product Image — large thumbnail ──────────────────────────────────
    {
      accessor: 'productImage',
      header: SMART_COOLER_COLUMNS.IMAGE,
      noTruncate: true,
      render: (item, idx) => (
        <div className="flex items-center justify-start">
          <Tooltip
            placement="right"
            unstyled
            arrow={false}
            content={
              <div className="w-52 h-52 rounded-xl overflow-hidden bg-white dark:bg-[#0f1623] p-3 shadow-none">
                <img
                  src={item.productImage || brokenImage}
                  alt="product preview"
                  className="w-full h-full object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).src = brokenImage; }}
                />
              </div>
            }
            className="p-0 rounded-xl border border-gray-200 dark:border-white/[0.10] shadow-2xl bg-white dark:bg-[#0f1623]"
          >
            <div className="w-16 h-16 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.10] bg-white dark:bg-white/[0.03] shadow-[0_1px_4px_0_rgba(0,0,0,0.08)] dark:shadow-[0_1px_6px_0_rgba(0,0,0,0.30)] cursor-zoom-in">
              <img
                src={item.productImage || brokenImage}
                alt="product"
                className="w-full h-full object-contain p-1.5"
                onError={(e) => { (e.target as HTMLImageElement).src = brokenImage; }}
              />
            </div>
          </Tooltip>
        </div>
      ),
    },

    // ── 2. Product Name — rich identity cell ─────────────────────────────────
    {
      accessor: 'shortProductName',
      header: SMART_COOLER_COLUMNS.PRODUCT_NAME,
      sorting: true,
      noTruncate: true,
      render: (item, idx) => {
        const short = item.shortProductName;
        const full = item.productName;
        const showFull = full && full !== short;
        const barcodeCount = item.barcodes?.length ?? 0;

        return (
          <Cell
            testId={tid(TID.COL_NAME(`${idx}`))}
            value={short || ''}
            variant="default"
            className="font-semibold max-w-[220px]"
            copy={{
              copyKey: `name-${item.smartCoolerProductId}`,
              isCopy,
              onCopyClick,
            }}
            subtitle={showFull ? full : undefined}
            meta={
              barcodeCount > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <MdQrCode2 className="h-3 w-3 text-primary opacity-70 flex-shrink-0" />
                  <span className="text-[10px] font-semibold tabular-nums text-gray-400 dark:text-gray-500">
                    {barcodeCount} barcode{barcodeCount !== 1 ? 's' : ''}
                  </span>
                </span>
              ) : undefined
            }
          />
        );
      },
    },

    // ── 3. SCP ID — mono chip with copy ─────────────────────────────────────
    {
      accessor: 'smartCoolerProductId',
      header: SMART_COOLER_COLUMNS.SCP_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_SCP_ID(`${idx}`))}
          value={item.smartCoolerProductId}
          tooltip
          variant="mono"
          copy={{
            copyKey: `scp-${item.smartCoolerProductId}`,
            isCopy,
            onCopyClick,
          }}
        />
      ),
    },

    // ── 4. Legacy ID — primary mono badge ────────────────────────────────────
    {
      accessor: 'legacyId',
      header: SMART_COOLER_COLUMNS.LEGACY_ID,
      sorting: true,
      render: (item, idx) => {
        if (!item.legacyId)
          return <Cell testId={tid(TID.COL_LEGACY_ID(`${idx}`))} value={undefined} variant="muted" />;
        return (
          <Cell
            testId={tid(TID.COL_LEGACY_ID(`${idx}`))}
            value={item.legacyId}
            tooltip={item.legacyId}
            variant="primary"
            copy={{
              copyKey: `leg-${item.smartCoolerProductId}`,
              isCopy,
              onCopyClick,
            }}
          />
        );
      },
    },

    // ── 6. Active — icon-only indicator ────────────────────────────────────
    {
      accessor: 'deletedOn',
      header: SMART_COOLER_COLUMNS.STATUS,
      noTruncate: true,
      cellClassName: '!text-center',
      headerClassName: 'text-center',
      render: (item, idx) => {
        const isInactive = Boolean(item.deletedOn);
        return (
          <StatusBadge
            variant={isInactive ? 'inactive' : 'active'}
            size="sm"
            tooltip={
              isInactive
                ? `Deleted on: ${getDateTime(item.deletedOn!)}`
                : 'Product is active'
            }
            testId={tid(TID.COL_STATUS(`${idx}`))}
          />
        );
      },
    },

    // ── 8. Updated On — relative time ─────────────────────────────────────────
    {
      accessor: 'updatedOn',
      header: SMART_COOLER_COLUMNS.UPDATED,
      sorting: true,
      render: ({ updatedOn, smartCoolerProductId }: any) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(smartCoolerProductId))}
          value={updatedOn}
          variant="muted"
          tooltip={getDateTime(updatedOn)}
          tooltipPlacement="top"
        >
          {updatedOn ? getDateTimeAgo(updatedOn) : '—'}
        </Cell>
      ),
    },

    // ── 9. Created On ─────────────────────────────────────────────────────────
    {
      accessor: 'createdOn',
      header: SMART_COOLER_COLUMNS.CREATED_ON,
      sorting: true,
      render: ({ createdOn, smartCoolerProductId }: any) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(smartCoolerProductId))}
          value={createdOn}
          format="datetime"
          variant="muted"
        />
      ),
    },

    // ── 10. Action ───────────────────────────────────────────────────────────
    {
      accessor: 'action',
      header: SMART_COOLER_COLUMNS.ACTION,
      render: (item, idx) => {
        if (getRole() !== ROLES.ADMIN) return null;
        return (
          <div
            className="flex items-center gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {!item.deletedOn && (
              <Tooltip content="Edit Product" placement="top">
                <AppButton
                  data-testid={tid(TID.COL_EDIT_BTN(`${idx}`))}
                  variant="info"
                  shape="circle"
                  size="xs"
                  hoverBg={false}
                  icon={<FiEdit2 className="h-3.5 w-3.5" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditClick(item);
                  }}
                />
              </Tooltip>
            )}
            {!item.deletedOn && (
              <Tooltip content="Delete Product" placement="top">
                <AppButton
                  data-testid={tid(TID.COL_DELETE_BTN(`${idx}`))}
                  variant="danger"
                  shape="circle"
                  tint
                  size="xs"
                  hoverBg={false}
                  icon={<FiTrash2 className="h-3.5 w-3.5" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteClick(item);
                  }}
                />
              </Tooltip>
            )}
            {item.deletedOn && (
              <Tooltip content="Activate Product" placement="top">
                <AppButton
                  data-testid={tid(TID.COL_ACTIVATE_BTN(`${idx}`))}
                  variant="success"
                  shape="circle"
                  tint
                  size="xs"
                  hoverBg={false}
                  icon={<FaUndo className="h-3.5 w-3.5 rotate-[-90deg]" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivateClick(item);
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
}
